import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Form, ListGroup } from 'react-bootstrap';
import { describeCondition, primaryOrderId } from '../lib/describe';
import { previewRule } from '../lib/previewRule';
import { LoadError, RateLabel } from './Common';
import { useRecentRateShopDecisions } from '../hooks/useRecentRateShopDecisions';
import { storeLabelForId } from '../lib/storeHelpers';
import { statusMeta } from '../config/rateShopConstants';
import ValuePicker from './ValuePicker';
import HelpTip from './HelpTip';

const RulePreview = ({
  draft,
  rules,
  settings,
  getDecision,
  valid,
  stores
}) => {
  const [shipmentId, setShipmentId] = useState('');
  const recent = useRecentRateShopDecisions();
  const [decision, setDecision] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const request = useRef(0);
  useEffect(
    () => () => {
      request.current += 1;
    },
    []
  );
  const result = useMemo(
    () =>
      decision && valid && settings
        ? previewRule(decision, draft, rules, settings)
        : null,
    [decision, valid, draft, rules, settings]
  );

  const load = async (id = shipmentId) => {
    const token = ++request.current;
    setLoading(true);
    setDecision(null);
    setError(null);
    try {
      const found = await getDecision(id.trim());
      if (token !== request.current) return;
      if (!found?.context)
        throw new Error('No stored decision context found for this shipment.');
      setDecision(found);
    } catch (err) {
      if (token === request.current) setError(err);
    } finally {
      if (token === request.current) setLoading(false);
    }
  };

  return (
    <Card className="bg-light border mt-3">
      <Card.Body>
        <h5>
          Test this rule{' '}
          <HelpTip label="Test this rule">
            Try your draft on an order already in the Log. This changes nothing
            in ShipStation.
          </HelpTip>
        </h5>
        <p className="small text-muted">
          Replays the draft and existing rules against stored context and rates.
          No ShipStation requests or changes are made. Rates absent from the
          original run cannot be simulated.
        </p>
        <Form.Label>
          Choose a recent order{' '}
          <HelpTip label="Recent orders">
            The 20 most recently logged decisions. Choose one to test this rule
            against its saved order details and prices.
          </HelpTip>
        </Form.Label>
        <ValuePicker
          label="Recent order to test"
          loading={recent.loading}
          value={shipmentId}
          onChange={id => {
            setShipmentId(id);
            if (id) load(id);
            else {
              request.current += 1;
              setDecision(null);
              setError(null);
              setLoading(false);
            }
          }}
          options={recent.decisions.map(d => {
            const primary = primaryOrderId(d);
            const customer = d.externalOrderId || d.context?.externalOrderId;
            return {
              value: d.id,
              label: `${primary}${
                customer && customer !== primary ? ` (${customer})` : ''
              } · ${storeLabelForId(
                d.storeId || d.context?.storeId,
                stores
              )} · ${statusMeta(d.status).label}`
            };
          })}
        />
        <LoadError error={recent.error} />
        <Form.Label className="mt-3">
          Or enter a shipment id{' '}
          <HelpTip label="Shipment id">
            Paste the ShipStation shipment id, beginning se-, to test an older
            order.
          </HelpTip>
        </Form.Label>
        <div className="d-flex gap-2 mb-3">
          <Form.Control
            aria-label="Preview shipment id"
            placeholder="se-… shipment id"
            value={shipmentId}
            onChange={e => setShipmentId(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (shipmentId.trim() && !loading) load();
              }
            }}
          />
          <Button
            disabled={!shipmentId.trim() || loading}
            onClick={() => load()}
          >
            {loading ? 'Loading…' : 'Test rule'}
          </Button>
        </div>
        <LoadError error={error} />
        {decision && !valid && (
          <Alert variant="warning">
            Correct the validation errors to replay this rule.
          </Alert>
        )}
        {result && (
          <>
            {result.missingFields.length > 0 && (
              <Alert variant="warning">
                Recorded context omits {result.missingFields.join(', ')}.
                Condition results and the simulated winner may differ from a
                fresh sweep.
              </Alert>
            )}
            <p>
              <Badge bg={result.matched ? 'success' : 'secondary'}>
                {result.matched ? 'Matched' : 'Not matched'}
              </Badge>{' '}
              <span className="small">Conditions for {decision.id}</span>
            </p>
            {draft.enabled === false && (
              <Alert variant="info">
                This rule is disabled. Its conditions are evaluated here, but
                its actions will not run.
              </Alert>
            )}
            {!result.trace.some(
              t => t.ruleId === (draft.id || 'preview-rule')
            ) && (
              <Alert variant="info">
                An earlier rule stopped processing before this rule could run.
              </Alert>
            )}
            {!result.conditions.length && (
              <p className="small">
                No conditions: this rule matches every order.
              </p>
            )}
            <ListGroup className="mb-3">
              {result.conditions.map((c, i) => (
                <ListGroup.Item key={i} className="small">
                  <Badge
                    bg={c.matched ? 'success' : 'secondary'}
                    className="me-2"
                  >
                    {c.matched ? 'True' : 'False'}
                  </Badge>
                  <strong>{c.group === 'all' ? 'All of' : 'Any of'}:</strong>{' '}
                  {describeCondition(c.condition, stores)}
                </ListGroup.Item>
              ))}
            </ListGroup>
            <p className="mb-1">
              <strong>Logged winner:</strong>{' '}
              <RateLabel rate={decision.winner} />
            </p>
            {result.simulationMessage ? (
              <Alert variant="info">{result.simulationMessage}</Alert>
            ) : (
              <>
                <p className="mb-1">
                  <strong>With this draft:</strong>{' '}
                  <RateLabel rate={result.winner} />
                </p>
                <p className="small mb-1">
                  {result.plan.skip ? 'Skipped: ' : ''}
                  {result.reason}
                </p>
              </>
            )}
            {result.notes.map((note, i) => (
              <p key={i} className="small text-warning mb-0">
                {note}
              </p>
            ))}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default RulePreview;
